/**
 * Canonical status → chip-variant mapping. Single source of truth for every
 * status chip across the frontend (positions, schedule, orders, modals).
 *
 * Rules (derived from usage frequency at the time of consolidation):
 *   COMPLETE / SUCCESS                                 → green  (terminal success)
 *   OPEN                                               → blue   (position is live)
 *   TRIGGER_PENDING / ENTRY_PENDING / EXIT_PENDING / PARTIAL
 *                                                      → yellow (in-flight / awaiting)
 *   PENDING_PLACE                                      → placing (claim in-flight, ~1s)
 *   REJECTED / FAILED                                  → red    (user-actionable error)
 *   CLOSED / CANCELLED                                 → gray   (inactive / historical)
 *   anything else / null                               → gray   (safe fallback)
 */
import {
  POSITION_STATUS,
  ENTRY_STATUS,
  ORDER_STATUS,
} from '../constants/tradingConstants';
import { CHIP_DENSE } from './chipColors';

export type StatusVariant = 'green' | 'yellow' | 'red' | 'gray' | 'blue' | 'placing' | 'slate';

// Variant → chip-color class. Sourced from `chipColors.CHIP_DENSE` so every
// status chip uses the canonical palette (text-800 light, etc.). Adding /
// changing colors requires editing `chipColors.ts`, not this file.
export const STATUS_VARIANT_CLASSES: Record<StatusVariant, string> = {
  green:   CHIP_DENSE.COMPLETE,        // green-100 / green-800
  yellow:  CHIP_DENSE.TRIGGER_PENDING, // yellow-100 / yellow-800
  red:     CHIP_DENSE.REJECTED,        // red-100 / red-800 (canonical bump from 700)
  gray:    CHIP_DENSE.CANCELLED,       // gray-100 / gray-600
  blue:    CHIP_DENSE.OPEN,            // blue-100 / blue-800
  placing: CHIP_DENSE.PENDING_PLACE,   // gray-100 / gray-700 (claim in-flight)
  slate:   CHIP_DENSE.TERMINATED,      // slate-100 / slate-700 (platform stopped watching)
};

/**
 * Optional friendly display label for a raw status enum value. Returns
 * undefined for statuses that display fine as-is (e.g. COMPLETE, REJECTED).
 * Used by StatusChip so `PENDING_PLACE` shows as "Placing…" instead of the
 * ugly raw enum. Kept next to `getStatusVariant` so labels and colors are
 * defined together.
 */
export function getStatusLabel(status: string | null | undefined): string | undefined {
  if (!status) return undefined;
  switch (status) {
    case ENTRY_STATUS.PENDING_PLACE: // same string as SL_STATUS.PENDING_PLACE
      return 'Placing…';
    case POSITION_STATUS.TERMINATED: // raw enum is shouty — title-case it
      return 'Terminated';
    // Phase 5e.H4: UL W&T invisible-wait — no broker order; the row is armed
    // and the monitor is watching spot. Distinct label so the leg-level
    // chips in StrategyPositionsModalV2 / PositionTableView / LegSection
    // don't render the raw "UL_MONITOR_PENDING" enum (gray, indistinguishable
    // from CLOSED). Wording chosen to hint at the mechanism (spot-based)
    // without over-specifying — the row's normalized wat_config.spotTrigger /
    // referenceSpot carry the actual level.
    case ENTRY_STATUS.UL_MONITOR_PENDING:
      return 'Waiting on spot';
    default:
      return undefined;
  }
}

export function getStatusVariant(status: string | null | undefined): StatusVariant {
  if (!status) return 'gray';
  switch (status) {
    case ENTRY_STATUS.COMPLETE:        // same string as EXIT_STATUS.COMPLETE / SL_STATUS.COMPLETE
    case ORDER_STATUS.SUCCESS:
      return 'green';

    case POSITION_STATUS.OPEN:         // same string as ENTRY_STATUS.OPEN etc.
    case 'ACTIVE':                     // aggregate: strategy is running cleanly
      return 'blue';

    case ENTRY_STATUS.TRIGGER_PENDING: // same as SL_STATUS.TRIGGER_PENDING
    case POSITION_STATUS.ENTRY_PENDING:
    case POSITION_STATUS.EXIT_PENDING:
    case ORDER_STATUS.PARTIAL:
    case ENTRY_STATUS.UL_MONITOR_PENDING: // Phase 5e.H4 — UL W&T invisible-wait (armed, awaiting spot)
    case 'PENDING':                    // aggregate: something is in-flight / awaiting
      return 'yellow';

    case ENTRY_STATUS.PENDING_PLACE:   // same as SL_STATUS.PENDING_PLACE — claim in-flight
      return 'placing';

    case ENTRY_STATUS.REJECTED:        // same as EXIT_STATUS.REJECTED / SL_STATUS.REJECTED
    case ORDER_STATUS.FAILED:
    case 'ERROR':                      // aggregate: a leg is rejected / user-actionable
      return 'red';

    case POSITION_STATUS.TERMINATED:   // platform-only stop-watching marker —
      return 'slate';                  // distinct neutral chip, NOT gray/CLOSED

    case POSITION_STATUS.CLOSED:
    case ENTRY_STATUS.CANCELLED:       // same as EXIT_STATUS.CANCELLED / SL_STATUS.CANCELLED / ORDER_STATUS.CANCELLED
    case 'SCHEDULED':                  // aggregate: future day / no positions yet
      return 'gray';

    default:
      return 'gray';
  }
}
